'use client';

import {
  Close as DialogClosePrimitive,
  Content as DialogContentPrimitive,
  Description as DialogDescriptionPrimitive,
  Overlay as DialogOverlayPrimitive,
  Portal as DialogPortalPrimitive,
  Root as DialogRootPrimitive,
  Title as DialogTitlePrimitive,
  Trigger as DialogTriggerPrimitive,
} from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef, ComponentPropsWithRef, HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const DialogPortal = DialogPortalPrimitive;
const Dialog = DialogRootPrimitive;
const DialogTrigger = DialogTriggerPrimitive;
const DialogClose = DialogClosePrimitive;

type OverlayProperties = ComponentPropsWithoutRef<typeof DialogOverlayPrimitive> & {
  ref?: ComponentPropsWithRef<typeof DialogOverlayPrimitive>['ref'];
};

const DialogOverlay = ({ className, ref, ...properties }: OverlayProperties) => (
  <DialogOverlayPrimitive
    ref={ref}
    className={cn(
      'fixed inset-0 z-layer-modal-backdrop bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...properties}
  />
);

type DialogContentProperties = ComponentPropsWithoutRef<typeof DialogContentPrimitive> & {
  ref?: ComponentPropsWithRef<typeof DialogContentPrimitive>['ref'];
};

const DialogContent = ({ className, children, ref, ...properties }: DialogContentProperties) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogContentPrimitive
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-layer-modal grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
        className
      )}
      {...properties}
    >
      {children}
      <DialogClosePrimitive className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogClosePrimitive>
    </DialogContentPrimitive>
  </DialogPortal>
);

const DialogHeader = ({ className, ...properties }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...properties} />
);

const DialogFooter = ({ className, ...properties }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...properties} />
);

type TitleProperties = ComponentPropsWithoutRef<typeof DialogTitlePrimitive> & {
  ref?: ComponentPropsWithRef<typeof DialogTitlePrimitive>['ref'];
};

const DialogTitle = ({ className, ref, ...properties }: TitleProperties) => (
  <DialogTitlePrimitive
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...properties}
  />
);

type DescriptionProperties = ComponentPropsWithoutRef<typeof DialogDescriptionPrimitive> & {
  ref?: ComponentPropsWithRef<typeof DialogDescriptionPrimitive>['ref'];
};

const DialogDescription = ({ className, ref, ...properties }: DescriptionProperties) => (
  <DialogDescriptionPrimitive ref={ref} className={cn('text-sm text-muted-foreground', className)} {...properties} />
);

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
